import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import type { BandOracleStatus } from '@/hooks/useBandOracleStatus';
import type { PodOracleStatus } from '@/hooks/usePodOracleStatus';

export type OracleStatusSource = 'band' | 'pod';

interface OracleStatusCardProps {
  source: OracleStatusSource;
  bandStatus?: BandOracleStatus | null;
  podStatus?: PodOracleStatus | null;
  isLoading: boolean;
  error: string | null;
  getExplorerUrl: (address: string) => string;
}

interface DisplayToken {
  symbol: string;
  /** Secondary pair label, e.g. "ETH/USD". */
  pairLabel: string;
  priceUsd: number | null;
  lastUpdated: Date | null;
  isStale: boolean;
  error: string | null;
}

type Freshness = 'fresh' | 'stale' | 'unknown';

/** Feeds older than this are never shown as Fresh in the backoffice. */
const MAX_FRESH_AGE_SECONDS = 60 * 60;

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};

const formatPrice = (price: number): string =>
  price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: price < 1 ? 6 : 2,
  });

const FreshnessFlag = ({
  freshness,
  ageSeconds,
}: {
  freshness: Freshness;
  ageSeconds: number | null;
}) => {
  const dotClass =
    freshness === 'stale'
      ? 'bg-red-500'
      : freshness === 'fresh'
        ? 'bg-green-500'
        : 'bg-muted-foreground/50';
  const textClass =
    freshness === 'stale'
      ? 'text-red-500'
      : freshness === 'fresh'
        ? 'text-green-600 dark:text-green-500'
        : 'text-muted-foreground';
  const label = freshness === 'stale' ? 'Stale' : freshness === 'fresh' ? 'Fresh' : 'Unknown';

  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className={`h-2.5 w-2.5 rounded-full ${dotClass}`} aria-hidden="true" />
      <span className={`text-xs font-medium ${textClass}`}>
        {label}
        {ageSeconds != null && ` (${formatDuration(ageSeconds)} ago)`}
      </span>
    </span>
  );
};

/** Resolve Fresh / Stale / Unknown without treating a missing timestamp as Stale. */
function resolveFreshness(tokenIsStale: boolean, ageSeconds: number | null): Freshness {
  if (tokenIsStale) return 'stale';
  if (ageSeconds == null) return 'unknown';
  if (ageSeconds > MAX_FRESH_AGE_SECONDS) return 'stale';
  return 'fresh';
}

function BandDescription({
  status,
  getExplorerUrl,
}: {
  status: BandOracleStatus | null;
  getExplorerUrl: (address: string) => string;
}) {
  if (!status) {
    return <>Last update per token from the Band price reference.</>;
  }
  return (
    <>
      Last update per token from the{' '}
      <a
        href={getExplorerUrl(status.refAddress)}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
      >
        Band reference
      </a>
      . This view marks feeds older than {formatDuration(MAX_FRESH_AGE_SECONDS)} as stale. On-chain, the{' '}
      <a
        href={getExplorerUrl(status.consumerAddress)}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
      >
        price consumer
      </a>{' '}
      reverts after {formatDuration(status.maxStalenessSeconds)}, hiding USD prices on the portal.
    </>
  );
}

function PodDescription({
  status,
  getExplorerUrl,
}: {
  status: PodOracleStatus | null;
  getExplorerUrl: (address: string) => string;
}) {
  if (!status) {
    return <>Last update per token from the PoD price oracle.</>;
  }
  return (
    <>
      Last update per token from the{' '}
      <a
        href={getExplorerUrl(status.oracleAddress)}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:text-foreground"
      >
        PoD price oracle
      </a>
      {status.configuredOracleAddress && (
        <>
          {' '}
          via the{' '}
          <a
            href={getExplorerUrl(status.configuredOracleAddress)}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            Band / Chainlink adapter
          </a>
        </>
      )}
      . This view marks feeds older than {formatDuration(MAX_FRESH_AGE_SECONDS)} as stale. A zero or
      missing price is also stale and portals fall back to the fixed fee.
    </>
  );
}

function toDisplayTokens(
  source: OracleStatusSource,
  bandStatus: BandOracleStatus | null,
  podStatus: PodOracleStatus | null,
): DisplayToken[] {
  if (source === 'band' && bandStatus) {
    return bandStatus.tokens.map((token) => ({
      symbol: token.symbol,
      pairLabel: `${token.base}/USD`,
      priceUsd: token.priceUsd,
      lastUpdated: token.lastUpdated,
      isStale: token.isStale,
      error: token.error,
    }));
  }
  if (source === 'pod' && podStatus) {
    return podStatus.tokens.map((token) => ({
      symbol: token.symbol,
      pairLabel: `${token.symbol}/USD`,
      priceUsd: token.priceUsd,
      lastUpdated: token.lastUpdated,
      isStale: token.isStale,
      error: token.error,
    }));
  }
  return [];
}

/** Oracle feed health for COTI (Band) or PoD (Sepolia/Fuji) — same card layout on both. */
const OracleStatusCard = ({
  source,
  bandStatus = null,
  podStatus = null,
  isLoading,
  error,
  getExplorerUrl,
}: OracleStatusCardProps) => {
  const nowSeconds = Date.now() / 1000;
  const isBand = source === 'band';
  const status = isBand ? bandStatus : podStatus;
  const tokens = toDisplayTokens(source, bandStatus, podStatus);

  return (
    <Card className="glass box-glow">
      <CardHeader>
        <CardTitle className="text-glow">
          {isBand ? 'Band Oracle Feeds' : 'Price Oracle Feeds'}
        </CardTitle>
        <CardDescription>
          {isBand ? (
            <BandDescription status={bandStatus} getExplorerUrl={getExplorerUrl} />
          ) : (
            <PodDescription status={podStatus} getExplorerUrl={getExplorerUrl} />
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error ? (
          <Alert variant="destructive" className="glass">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Oracle unreachable</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : isLoading && !status ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : tokens.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-2">Token</TableHead>
                  <TableHead className="px-2 text-right">Price (USD)</TableHead>
                  <TableHead className="px-2">Last Update</TableHead>
                  <TableHead className="px-2">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tokens.map((token) => {
                  const ageSeconds = token.lastUpdated
                    ? nowSeconds - token.lastUpdated.getTime() / 1000
                    : null;
                  const freshness = resolveFreshness(token.isStale, ageSeconds);
                  return (
                    <TableRow key={token.symbol}>
                      <TableCell className="px-2">
                        <span className="font-medium">{token.symbol}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{token.pairLabel}</span>
                      </TableCell>
                      <TableCell className="px-2 text-right tabular-nums">
                        {token.priceUsd != null ? `$ ${formatPrice(token.priceUsd)}` : '—'}
                      </TableCell>
                      <TableCell className="px-2 whitespace-nowrap tabular-nums">
                        {token.lastUpdated ? token.lastUpdated.toLocaleString() : token.error ?? '—'}
                      </TableCell>
                      <TableCell className="px-2">
                        <FreshnessFlag freshness={freshness} ageSeconds={ageSeconds} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

/** @deprecated Prefer OracleStatusCard with source="band"; kept for existing imports. */
const BandOracleStatusCard = ({
  status,
  isLoading,
  error,
  getExplorerUrl,
}: {
  status: BandOracleStatus | null;
  isLoading: boolean;
  error: string | null;
  getExplorerUrl: (address: string) => string;
}) => (
  <OracleStatusCard
    source="band"
    bandStatus={status}
    isLoading={isLoading}
    error={error}
    getExplorerUrl={getExplorerUrl}
  />
);

export { OracleStatusCard };
export default BandOracleStatusCard;
